// frontend/src/pages/reports/AlwaysIncludeCodesField.jsx
//
// "Always include these items" panel shared by the Stock Report, Stock
// Movements, Inventory Value on Date, BOM Usage and Raw Material Usage
// reports. State comes from
// useAlwaysInclude() (one instance in Reports.js) so the card and both
// dialogs always show the same thing.
//
// Two sources feed the always-include set:
//   1. Hand-picked items — a searchable multi-select with chips. Type a code
//      or name and pick from the dropdown (or "add all N matches"); paste a
//      comma / new-line list of codes and each is resolved against the item
//      master. Unknown codes stay as red chips. "Save" persists the picks.
//   2. Uploaded code sheets — drop in an .xlsx/.csv; every worksheet is
//      saved with a tick. Ticked sheets contribute all their codes. Ticks are
//      saved the moment they change (shared by everyone).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Collapse, Divider,
  IconButton, Link, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Check, Close, ContentCopy, DeleteOutline, DriveFileRenameOutline, ExpandLess, ExpandMore,
  Download, Inventory2Outlined, PlaylistAdd, PlaylistRemove, Save, Search, SortByAlpha, TableChart,
  UploadFile,
} from '@mui/icons-material';
import debounce from 'lodash/debounce';
import { toast } from 'react-toastify';

import { manufacturingAPI } from '../../services/api';

const SPLIT_RE = /[,;\s]+/;
const CHIP_PREVIEW = 30;
const ADD_ALL = '__add_all__';

const typeColor = (t) => {
  switch (t) {
    case 'raw_material': return 'default';
    case 'manufactured': return 'secondary';
    case 'assembly': return 'info';
    case 'finished_good': return 'success';
    default: return 'default';
  }
};

const num = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return `${Math.round(n * 1000) / 1000}`;
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Split pasted / typed text into upper-cased, de-duplicated codes. */
export const splitCodes = (text) => {
  const seen = new Set();
  const out = [];
  String(text || '').split(SPLIT_RE).forEach((raw) => {
    const c = raw.trim().toUpperCase();
    if (c && !seen.has(c)) { seen.add(c); out.push(c); }
  });
  return out;
};

/** Resolve codes via the server; unknown ones come back as placeholder rows. */
export const resolveCodes = async (codes) => {
  if (!codes.length) return [];
  try {
    const res = await manufacturingAPI.reports.itemSearch({ codes: codes.join(',') });
    const found = res.data?.results || [];
    const unknown = res.data?.unknown || [];
    return [
      ...found,
      ...unknown.map((c) => ({ item_code: c, name: '', unknown: true })),
    ];
  } catch (err) {
    console.error('Failed to resolve item codes', err);
    return codes.map((c) => ({ item_code: c, name: '', unknown: true }));
  }
};

// ---------------------------------------------------------------- picker

function ItemPicker({ value, onChange }) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showAllChips, setShowAllChips] = useState(false);
  const requestSeq = useRef(0);

  const selectedCodes = useMemo(
    () => new Set(value.map((v) => v.item_code.toUpperCase())),
    [value],
  );
  const unknownCount = value.filter((v) => v.unknown).length;
  const inactiveCount = value.filter((v) => !v.unknown && v.is_active === false).length;

  const runSearch = useMemo(() => debounce(async (q) => {
    const seq = ++requestSeq.current;
    try {
      const res = await manufacturingAPI.reports.itemSearch({ q: q.trim() });
      if (seq !== requestSeq.current) return; // stale response
      setOptions(res.data?.results || []);
    } catch (err) {
      if (seq === requestSeq.current) setOptions([]);
    } finally {
      if (seq === requestSeq.current) setSearching(false);
    }
  }, 250), []);

  useEffect(() => () => runSearch.cancel(), [runSearch]);

  const handleInputChange = (_, text, reason) => {
    if (reason === 'reset') return; // MUI clears input after a pick; keep options
    setInputValue(text);
    if (text.trim()) {
      setSearching(true);
      runSearch(text);
    } else {
      runSearch.cancel();
      setOptions([]);
      setSearching(false);
    }
  };

  /** Add rows to the selection, skipping codes already present. */
  const addItems = (rows) => {
    const next = [...value];
    rows.forEach((r) => {
      const code = (r.item_code || '').toUpperCase();
      if (code && !next.some((n) => n.item_code.toUpperCase() === code)) next.push(r);
    });
    onChange(next);
  };

  // Search results not already picked — what "add all" would add.
  const addable = options.filter((o) => !selectedCodes.has(o.item_code.toUpperCase()));

  const handleChange = async (_, newValue, reason) => {
    if (reason === 'removeOption' || reason === 'clear') {
      onChange(newValue.filter((v) => typeof v === 'object' && v.item_code !== ADD_ALL));
      return;
    }
    if (newValue.some((v) => v && v.item_code === ADD_ALL)) {
      addItems(addable);
      setInputValue('');
      setOptions([]);
      return;
    }
    // A pick from the list arrives as an object; a typed/pasted entry as a
    // string (freeSolo). Strings may hold a whole list of codes.
    const objects = newValue.filter((v) => typeof v === 'object');
    const typed = newValue.filter((v) => typeof v === 'string');
    const existing = objects.filter((o) => selectedCodes.has(o.item_code.toUpperCase()));
    const picked = objects.filter((o) => !selectedCodes.has(o.item_code.toUpperCase()));
    let resolved = [];
    if (typed.length) {
      const codes = splitCodes(typed.join(','))
        .filter((c) => !selectedCodes.has(c) && !picked.some((p) => p.item_code.toUpperCase() === c));
      resolved = await resolveCodes(codes);
    }
    onChange([...existing, ...picked, ...resolved]);
    setInputValue('');
    setOptions([]);
  };

  // Paste "A, B, C" straight into the box: resolve the lot at once rather
  // than treating it as one search string.
  const handlePaste = async (e) => {
    const text = e.clipboardData?.getData('text') || '';
    if (!SPLIT_RE.test(text.trim())) return; // single token — let search handle it
    e.preventDefault();
    const codes = splitCodes(text).filter((c) => !selectedCodes.has(c));
    if (!codes.length) return;
    setSearching(true);
    const rows = await resolveCodes(codes);
    setSearching(false);
    addItems(rows);
    setInputValue('');
  };

  // --- bulk actions on the chips ---
  const sortChips = () => onChange([...value].sort((a, b) => a.item_code.localeCompare(b.item_code)));
  const removeUnknown = () => onChange(value.filter((v) => !v.unknown));
  const clearAll = () => onChange([]);
  const copyCodes = async () => {
    const text = value.map((v) => v.item_code).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.info(`Copied ${plural(value.length, 'code')}`);
    } catch (err) {
      toast.error('Clipboard not available');
    }
  };

  const helper = (() => {
    if (unknownCount > 0) {
      return `${plural(unknownCount, 'code')} not found — fix or remove the red chip${unknownCount === 1 ? '' : 's'}`;
    }
    if (inactiveCount > 0) {
      return `${plural(inactiveCount, 'inactive item')} — inactive items are left out of reports`;
    }
    return 'Search by code or name, or paste a list of codes. Included whatever the filters say.';
  })();

  // Dropdown list: a synthetic "add all" row on top when several results fit.
  const listOptions = addable.length > 1
    ? [{ item_code: ADD_ALL, name: '', count: addable.length }, ...options]
    : options;

  const hiddenChips = !showAllChips && value.length > CHIP_PREVIEW ? value.length - CHIP_PREVIEW : 0;

  return (
    <>
      <Autocomplete
        multiple
        freeSolo
        autoHighlight
        clearOnBlur={false}
        filterSelectedOptions
        size="small"
        options={listOptions}
        value={value}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        loading={searching}
        loadingText="Searching…"
        noOptionsText={inputValue.trim() ? 'No matching items' : 'Type a code or name'}
        filterOptions={(x) => x} // server-side search, don't re-filter
        getOptionLabel={(o) => (typeof o === 'string' ? o : o.item_code)}
        isOptionEqualToValue={(o, v) => o.item_code?.toUpperCase() === v.item_code?.toUpperCase()}
        renderOption={(props, option) => {
          if (option.item_code === ADD_ALL) {
            return (
              <Box
                component="li"
                {...props}
                key={ADD_ALL}
                data-testid="always-include-add-all"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, bgcolor: 'action.hover' }}
              >
                <PlaylistAdd sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography variant="body2" fontWeight={700} color="primary.main">
                  Add all {option.count} matches for &ldquo;{inputValue.trim()}&rdquo;
                </Typography>
              </Box>
            );
          }
          return (
            <Box
              component="li"
              {...props}
              key={option.item_code}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}
            >
              <Inventory2Outlined sx={{ fontSize: 18, color: 'text.disabled' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} noWrap>{option.item_code}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {option.name}
                </Typography>
              </Box>
              <Chip
                label={option.item_type_label || option.item_type}
                size="small"
                variant="outlined"
                color={typeColor(option.item_type)}
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
              <Box sx={{ textAlign: 'right', minWidth: 72 }}>
                <Typography variant="body2" fontWeight={600}>{num(option.quantity_on_hand)}</Typography>
                <Typography
                  variant="caption"
                  sx={{ color: option.counted ? 'success.main' : 'warning.main', display: 'block' }}
                >
                  {option.counted ? 'counted' : 'not counted'}
                </Typography>
              </Box>
            </Box>
          );
        }}
        renderTags={(tagValue, getTagProps) => {
          const shown = showAllChips ? tagValue : tagValue.slice(0, CHIP_PREVIEW);
          return [
            ...shown.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              const unknown = !!option.unknown;
              const inactive = !unknown && option.is_active === false;
              const title = unknown
                ? 'No item with this code'
                : `${option.name || ''}${inactive ? ' (inactive)' : ''} · ${num(option.quantity_on_hand)} on hand · ${option.counted ? 'stock-counted' : 'never stock-counted (always included anyway)'}`;
              return (
                <Tooltip title={title} key={key} arrow>
                  <Chip
                    {...tagProps}
                    label={option.item_code}
                    size="small"
                    color={unknown ? 'error' : inactive ? 'warning' : 'primary'}
                    variant={unknown ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 600 }}
                    data-testid={`always-include-chip-${option.item_code}`}
                  />
                </Tooltip>
              );
            }),
            hiddenChips > 0 && (
              <Chip
                key="__more"
                label={`+${hiddenChips} more`}
                size="small"
                onClick={() => setShowAllChips(true)}
                sx={{ fontWeight: 600 }}
                data-testid="always-include-more-chips"
              />
            ),
          ];
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Always include these items"
            placeholder={value.length ? 'Add another…' : 'Search by code or name, or paste a list'}
            error={unknownCount > 0}
            helperText={helper}
            onPaste={handlePaste}
            inputProps={{ ...params.inputProps, 'data-testid': 'always-include-codes-input' }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <Search sx={{ fontSize: 18, color: 'text.disabled', ml: 0.5, mr: 0.5 }} />
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {searching ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {value.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.25, flexWrap: 'wrap', alignItems: 'center' }}>
          <Tooltip title="Sort chips A–Z">
            <IconButton size="small" onClick={sortChips} data-testid="always-include-sort"><SortByAlpha fontSize="inherit" /></IconButton>
          </Tooltip>
          <Tooltip title="Copy all codes">
            <IconButton size="small" onClick={copyCodes} data-testid="always-include-copy"><ContentCopy fontSize="inherit" /></IconButton>
          </Tooltip>
          {unknownCount > 0 && (
            <Tooltip title="Remove codes that match no item">
              <IconButton size="small" color="error" onClick={removeUnknown} data-testid="always-include-remove-unknown">
                <PlaylistRemove fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
          {showAllChips && value.length > CHIP_PREVIEW && (
            <Button size="small" onClick={() => setShowAllChips(false)} sx={{ textTransform: 'none' }}>collapse</Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button size="small" color="inherit" onClick={clearAll} sx={{ textTransform: 'none', color: 'text.secondary' }} data-testid="always-include-clear">
            Clear all
          </Button>
        </Stack>
      )}
    </>
  );
}

// ---------------------------------------------------------------- sheets

function SheetRow({ sheet, busy, onToggle, onRename, onDelete }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(sheet.name);

  const commitRename = () => {
    const name = draft.trim();
    setEditing(false);
    if (name && name !== sheet.name) onRename(sheet.id, name);
    else setDraft(sheet.name);
  };

  const uploaded = sheet.created_at ? new Date(sheet.created_at).toLocaleDateString() : '';

  return (
    <Box
      data-testid={`code-sheet-${sheet.id}`}
      sx={{
        border: '1px solid', borderColor: sheet.enabled ? 'primary.light' : 'divider',
        bgcolor: sheet.enabled ? 'primary.50' : 'transparent', borderRadius: 1, px: 1, py: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Checkbox
          size="small"
          checked={!!sheet.enabled}
          disabled={busy}
          onChange={(e) => onToggle(sheet.id, e.target.checked)}
          inputProps={{ 'data-testid': `code-sheet-tick-${sheet.id}` }}
          sx={{ p: 0.5 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {editing ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField
                size="small"
                value={draft}
                autoFocus
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename();
                  if (e.key === 'Escape') { setDraft(sheet.name); setEditing(false); }
                }}
                inputProps={{ 'data-testid': `code-sheet-rename-${sheet.id}`, style: { padding: '2px 6px', fontSize: '0.85rem' } }}
                sx={{ width: 220 }}
              />
              <IconButton size="small" color="primary" onClick={commitRename}><Check fontSize="inherit" /></IconButton>
              <IconButton size="small" onClick={() => { setDraft(sheet.name); setEditing(false); }}><Close fontSize="inherit" /></IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight={700}
                noWrap
                onDoubleClick={() => setEditing(true)}
                title="Double-click to rename"
              >
                {sheet.name}
              </Typography>
              <Tooltip title="Rename">
                <IconButton size="small" onClick={() => setEditing(true)} sx={{ p: 0.25 }}>
                  <DriveFileRenameOutline sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {sheet.file_name}
            {sheet.sheet_name && sheet.sheet_name !== sheet.name ? ` › ${sheet.sheet_name}` : ''}
            {uploaded ? ` · ${uploaded}` : ''}
            {sheet.uploaded_by ? ` · ${sheet.uploaded_by}` : ''}
          </Typography>
        </Box>
        <Chip label={plural(sheet.code_count, 'code')} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
        {sheet.unknown_count > 0 && (
          <Tooltip title="Codes on this sheet that match no item — they are ignored">
            <Chip label={`${sheet.unknown_count} unknown`} size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
          </Tooltip>
        )}
        <Tooltip title={open ? 'Hide codes' : 'Show codes'}>
          <IconButton size="small" onClick={() => setOpen((o) => !o)} data-testid={`code-sheet-expand-${sheet.id}`}>
            {open ? <ExpandLess fontSize="inherit" /> : <ExpandMore fontSize="inherit" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Remove this sheet">
          <IconButton
            size="small"
            color="error"
            disabled={busy}
            onClick={() => {
              // eslint-disable-next-line no-alert
              if (window.confirm(`Remove sheet "${sheet.name}" (${plural(sheet.code_count, 'code')})?`)) onDelete(sheet.id);
            }}
            data-testid={`code-sheet-delete-${sheet.id}`}
          >
            <DeleteOutline fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pt: 0.5, pb: 0.5, pl: 4 }}>
          {(sheet.codes || []).map((c) => (
            <Chip key={c} label={c} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function SheetsPanel({ state, compact }) {
  const {
    sheets, sheetsBusy, uploading, uploadSheet, downloadTemplate, setSheetEnabled, setAllSheets,
    renameSheet, deleteSheet,
  } = state;
  const [open, setOpen] = useState(!compact);
  const fileRef = useRef(null);
  const ticked = sheets.filter((s) => s.enabled).length;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) {
      await uploadSheet(file);
      setOpen(true);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          onClick={() => setOpen((o) => !o)}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}
          data-testid="code-sheets-toggle"
        >
          <TableChart sx={{ fontSize: 16 }} />
          Code sheets
          {sheets.length > 0 && ` (${ticked} of ${sheets.length} ticked)`}
          {open ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
        </Link>
        <Box sx={{ flex: 1 }} />
        {sheets.length > 1 && (
          <>
            <Button size="small" disabled={sheetsBusy || ticked === sheets.length} onClick={() => setAllSheets(true)} sx={{ textTransform: 'none' }} data-testid="code-sheets-all">
              Tick all
            </Button>
            <Button size="small" disabled={sheetsBusy || ticked === 0} onClick={() => setAllSheets(false)} sx={{ textTransform: 'none' }} data-testid="code-sheets-none">
              Untick all
            </Button>
          </>
        )}
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xlsm,.csv"
          hidden
          onChange={onPick}
          data-testid="code-sheet-file"
        />
        <Tooltip title="Blank workbook with a Code column to fill in and upload">
          <Button
            size="small"
            startIcon={<Download />}
            onClick={downloadTemplate}
            sx={{ textTransform: 'none' }}
            data-testid="code-sheet-template"
          >
            Template
          </Button>
        </Tooltip>
        <Button
          size="small"
          variant="outlined"
          startIcon={uploading ? <CircularProgress size={14} /> : <UploadFile />}
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          sx={{ textTransform: 'none' }}
          data-testid="code-sheet-upload"
        >
          {uploading ? 'Reading…' : 'Upload Excel / CSV'}
        </Button>
      </Box>
      <Collapse in={open}>
        {sheets.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            No sheets yet. Download the template (or any workbook with the codes in a column headed
            &ldquo;Code&rdquo;, else column A) and upload it. Every worksheet is saved separately so you
            can tick the ones to use.
          </Typography>
        ) : (
          <Stack spacing={0.5} sx={{ mt: 0.75 }}>
            {sheets.map((s) => (
              <SheetRow
                key={s.id}
                sheet={s}
                busy={sheetsBusy}
                onToggle={setSheetEnabled}
                onRename={renameSheet}
                onDelete={deleteSheet}
              />
            ))}
          </Stack>
        )}
      </Collapse>
    </Box>
  );
}

// ---------------------------------------------------------------- panel

export default function AlwaysIncludeCodesField({ state, compact = false }) {
  const { items, setItems, dirty, saving, save, effective, sheets } = state;
  const tickedSheets = sheets.filter((s) => s.enabled).length;

  return (
    <Box sx={{ mt: compact ? 1.5 : 2 }} data-testid="always-include-codes">
      <ItemPicker value={items} onChange={setItems} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant={dirty ? 'contained' : 'outlined'}
          startIcon={saving ? <CircularProgress size={14} /> : <Save />}
          disabled={saving || !dirty}
          onClick={save}
          data-testid="always-include-codes-save"
        >
          {saving ? 'Saving…' : 'Save selection'}
        </Button>
        <Typography variant="caption" color={dirty ? 'warning.main' : 'text.secondary'}>
          {dirty
            ? 'Unsaved — this run uses the items above; save to keep them for everyone.'
            : 'Shared with everyone.'}
        </Typography>
      </Box>

      <Divider sx={{ my: 1 }} />
      <SheetsPanel state={state} compact={compact} />

      <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 600 }} data-testid="always-include-summary">
        {effective.total === 0
          ? 'Nothing extra is included.'
          : `${plural(effective.total, 'item')} always included`
            + ` — ${effective.picked} picked`
            + (tickedSheets > 0 ? `, ${effective.fromSheets} more from ${plural(tickedSheets, 'ticked sheet')}` : '')
            + '.'}
      </Typography>
    </Box>
  );
}
